import { useEffect, useState } from "react";

/** Mỗi 3.5s đổi ảnh */
const CROSSFADE_INTERVAL_MS = 3500;

interface VoicebotDisplayProps {
  url: string | null;
  defaultImage: string;
  /** Ảnh thứ hai để transition crossfade với defaultImage (mỗi 3–4 giây) */
  defaultImage2?: string;
  isPlaying: boolean;
  onCloseIframe?: () => void;
  /** Hiển thị tại vị trí Standby: "Agent is looking...", Time, hoặc warning */
  statusMessage?: string | null;
}

const VoicebotDisplay = ({ url, defaultImage, defaultImage2, isPlaying, onCloseIframe, statusMessage }: VoicebotDisplayProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [image2Loaded, setImage2Loaded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const label = statusMessage ?? (isPlaying ? "Active" : "Standby");
  const useCrossfade = Boolean(defaultImage2);

  useEffect(() => {
    if (!useCrossfade) return;
    const t = setInterval(() => {
      setActiveIndex((i) => (i === 0 ? 1 : 0));
    }, CROSSFADE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [useCrossfade]);

  const bothLoaded = useCrossfade ? imageLoaded && image2Loaded : imageLoaded;

  return (
    <div className="voicebot-display w-full aspect-video max-h-[50vh] landscape:max-h-[60vh] sm:max-h-[55vh] lg:max-h-[65vh] relative">
      <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent pointer-events-none z-10" />

      <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
        <div
          className={`w-2.5 h-2.5 rounded-full shrink-0 ${
            isPlaying ? "bg-green-400 animate-pulse" : "bg-muted-foreground"
          }`}
        />
        <span className="text-xs sm:text-sm text-muted-foreground font-medium">
          {label}
        </span>
      </div>

      {/* Ảnh nền: 1 ảnh hoặc 2 ảnh crossfade */}
      <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden rounded-2xl">
        <img
          src={defaultImage}
          alt="AI Voicebot for Airline Customer Care"
          className={`absolute inset-0 w-full h-full object-cover rounded-2xl transition-opacity duration-[2000ms] ${
            useCrossfade
              ? activeIndex === 0 ? "opacity-100" : "opacity-0"
              : bothLoaded ? "opacity-100" : "opacity-0"
          } ${!useCrossfade ? "transition-all duration-700" : ""} ${!useCrossfade && isPlaying ? "scale-105" : "scale-100"}`}
          onLoad={() => setImageLoaded(true)}
        />
        {useCrossfade && defaultImage2 && (
          <img
            src={defaultImage2}
            alt=""
            aria-hidden
            className={`absolute inset-0 w-full h-full object-cover rounded-2xl transition-opacity duration-[2000ms] ${
              activeIndex === 1 ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImage2Loaded(true)}
          />
        )}
        {!bothLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Popup iframe khi có function call: đè lên ảnh, nằm trọn trong div, bo góc tròn như ảnh */}
      {url && (
        <div className="absolute inset-0 w-full h-full overflow-hidden rounded-2xl z-[5]">
          <iframe
            src={url}
            className="w-full h-full rounded-2xl border-0"
            title="Voicebot Content"
            allow="microphone; camera"
          />
          {onCloseIframe && (
            <button
              type="button"
              onClick={onCloseIframe}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-lg bg-black/60 hover:bg-black/80 text-white flex items-center justify-center text-lg leading-none transition-colors"
              aria-label="Đóng"
              title="Đóng"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-primary/30 rounded-tl-2xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-primary/30 rounded-tr-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-primary/30 rounded-bl-2xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-primary/30 rounded-br-2xl pointer-events-none" />
    </div>
  );
};

export default VoicebotDisplay;
